/**
 * smufl.js — Bravura (SMuFL) helpers for the MusicSheet score renderer.
 *
 * Codepoints verified against the SMuFL spec + glyph bounding boxes read
 * from Bravura.woff2 (unitsPerEm 1000, i.e. 1 em = 4 staff spaces).
 * All "sp" values below are staff spaces (1 sp = LINE_SPACING px = 10 px,
 * so Bravura text runs at font-size 40px).
 *
 * Glyph origins (from measured bboxes):
 * - noteheads: y=0 is the vertical CENTER, x=0 is the LEFT edge
 * - accidentals (sharp/flat/natural): y=0 is the vertical CENTER
 * - augmentationDot: centered on origin
 * - flags: origin = stem tip (up-stems extend downward, down-stems downward too)
 * - staccato/accent/marcato/tenuto/fermata(above): origin = BOTTOM of glyph
 * - dynamics letters: use dominant-baseline="central" on the <text>
 * - whole rest: hangs BELOW origin / half rest: sits ABOVE origin
 * - quarter/8th/16th/32nd rests: roughly centered, place origin near middle line
 *
 * NOTE: accidentalDoubleSharp (U+E263) measures ~1x1sp in this Bravura
 * build, which looks wrong — 𝄪/𝄫 keep rendering as system-font text.
 */

export const SMUFL_FONT_FAMILY = 'Bravura';
// 1 em = 4 staff spaces
export const SMUFL_SPACES_PER_EM = 4;

export const SMUFL = {
  noteheadBlack: '\uE0A4',
  noteheadHalf: '\uE0A3',
  noteheadWhole: '\uE0A2',
  noteheadXBlack: '\uE0A9',
  noteheadDiamondBlack: '\uE0DB',
  noteheadDiamondHalf: '\uE0D9',
  noteheadDiamondWhole: '\uE0D8',
  accidentalSharp: '\uE262',
  accidentalFlat: '\uE260',
  accidentalNatural: '\uE261',
  augmentationDot: '\uE1E7',
  flag8thUp: '\uE240',
  flag8thDown: '\uE241',
  flag16thUp: '\uE242',
  flag16thDown: '\uE243',
  flag32ndUp: '\uE244',
  flag32ndDown: '\uE245',
  articStaccatoAbove: '\uE4A2',
  articAccentAbove: '\uE4A0',
  articMarcatoAbove: '\uE4AC',
  articTenutoAbove: '\uE4A4',
  fermataAbove: '\uE4C0',
  downBow: '\uE4A8',
  upBow: '\uE4A9',
  dynamicPiano: '\uE520',
  dynamicMezzo: '\uE521',
  dynamicForte: '\uE522',
  dynamicSforzando: '\uE524',
  dynamicZ: '\uE526',
  restWhole: '\uE4E3',
  restHalf: '\uE4E4',
  restQuarter: '\uE4E5',
  rest8th: '\uE4E6',
  rest16th: '\uE4E7',
  rest32nd: '\uE4E8'
};

// Advance widths in staff spaces (measured), for manual horizontal layout
export const SMUFL_ADVANCE = {
  noteheadBlack: 1.18,
  noteheadHalf: 1.18,
  noteheadWhole: 1.69,
  noteheadXBlack: 1.16,
  noteheadDiamondBlack: 0.98,
  noteheadDiamondHalf: 0.98,
  noteheadDiamondWhole: 1.66,
  accidentalSharp: 1.0,
  accidentalFlat: 0.9,
  accidentalNatural: 0.67,
  dynamicPiano: 1.46,
  dynamicMezzo: 1.75,
  dynamicForte: 1.46,
  dynamicSforzando: 0.92,
  dynamicZ: 1.23
};

// Accidentals model value -> SMuFL glyph (null = keep legacy text, e.g. 𝄪/𝄫)
export function smuflAccidental(acc) {
  if (acc === '#') return SMUFL.accidentalSharp;
  if (acc === 'b') return SMUFL.accidentalFlat;
  if (acc === '=') return SMUFL.accidentalNatural;
  return null;
}

// Dynamics marking (e.g. "mf", "sfz") -> { items: [{ glyph, x }], width }
// x/width in staff spaces; glyphs use dominant-baseline="central"
export function smuflDynamics(marking) {
  const letters = { p: ['dynamicPiano', 1.46], m: ['dynamicMezzo', 1.75], f: ['dynamicForte', 1.46], s: ['dynamicSforzando', 0.92], z: ['dynamicZ', 1.23] };
  const items = [];
  let x = 0;
  for (const ch of String(marking || '').toLowerCase()) {
    const entry = letters[ch];
    if (!entry) continue;
    items.push({ glyph: SMUFL[entry[0]], x });
    x += entry[1];
  }
  return { items, width: x };
}
